/** Tag category → Vocabulary tab.
 *
 *  What this category declares, versus the standard schema and versus its own
 *  tag data. Read-only on purpose: the standard is *referenced* (`schema_ref`),
 *  never merged into a tenant's inline schema, because the effective schema
 *  feeds `validate_tag_dict`, which coerces by declared type on the
 *  notification-rendering path — its boolean coercion maps every value but the
 *  literal "true" to false, so a silent merge could start sending what a
 *  reseller has suppressed. Adopting the standard is therefore a deliberate act
 *  made on this page, in the General tab's schema section.
 *
 *  The flat-multilingual section is the one with a hidden consequence:
 *  `{% tagattr %}` reads stored tag data and cannot see `notes_en`. Declaring
 *  the base `x-multilingual` makes the platform fold those keys at read time —
 *  no data migration — so the table says which families still need that and
 *  which are already handled.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as tagCategoriesApi from '@/api/tagCategories';
import type { TagCategoryDetail, TagCategorySchemaDiff } from '@/api/tagCategories';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { vocabularyPropertyActions } from '../tagLinks';

/** An attribute name: copy it, or find every record that stores it. Scoped to
 *  the owning operator — `system` is the spelling find-replace expects for a
 *  system category. */
function PropertyName({ name, tenantCode }: { name: string; tenantCode: string }) {
  return (
    <ValueActions actions={vocabularyPropertyActions(name, tenantCode)}>{name}</ValueActions>
  );
}

function Section({
  title,
  count,
  caption,
  children,
  defaultExpanded,
}: {
  title: string;
  count: number;
  caption: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded ?? count > 0}
      sx={{ border: 1, borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip size="small" variant="outlined" label={count} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ pb: 1 }}>
          {caption}
        </Typography>
        {count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing to report.
          </Typography>
        ) : (
          children
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function StandardSummary({ diff }: { diff: TagCategorySchemaDiff }) {
  if (!diff.standard) {
    return (
      <Alert severity="info" variant="outlined">
        No standard schema is published for <code>{diff.code}</code> yet. The drafts for{' '}
        <code>product_info</code>, <code>subproduct</code>, <code>pickup</code>, <code>option</code>{' '}
        and <code>fallback_text</code> are still in property-by-property review.
      </Alert>
    );
  }
  return (
    <Alert
      severity={diff.standard.is_referenced ? 'success' : 'info'}
      variant="outlined"
      sx={{ '& code': { fontSize: '0.85em' } }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {diff.standard.is_referenced
          ? `This category references the standard “${diff.standard.name}”.`
          : `A standard “${diff.standard.name}” exists (${diff.standard.property_count} properties).`}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {diff.standard.description}
      </Typography>
      {!diff.standard.is_referenced && (
        <Typography variant="caption" component="div" sx={{ pt: 0.5 }}>
          Nothing is applied automatically. To adopt it, point <strong>Reusable schema</strong> at{' '}
          <code>{diff.standard.codename}</code> in the General tab — an inline schema, if any, keeps
          overriding it until you clear it.
        </Typography>
      )}
    </Alert>
  );
}

export function TagCategoryVocabularyTab({ category }: { category: TagCategoryDetail }) {
  const tenantCode = category.tenant?.code ?? 'system';
  const { data, isLoading, error } = useQuery({
    queryKey: ['tag_categories', 'schema_diff', category.id],
    queryFn: () => tagCategoriesApi.schemaDiff(category.id),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) return <Alert severity="error">Couldn’t load the vocabulary report.</Alert>;

  const needsMultilingual = data.flat_multilingual.filter((f) => !f.declared_multilingual);

  return (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      <StandardSummary diff={data} />

      <Section
        title="Missing from this category"
        count={data.missing_from_standard.length}
        caption="Properties the standard declares that this category's effective schema does not."
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Property</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.missing_from_standard.map((p) => (
              <TableRow key={p.name}>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  <PropertyName name={p.name} tenantCode={tenantCode} />
                </TableCell>
                <TableCell>{p.label}</TableCell>
                <TableCell>{p.type ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Type conflicts"
        count={data.type_conflicts.length}
        caption="Declared by both, with different types. Never resolved automatically — retyping changes how stored values are coerced when notifications render."
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Property</TableCell>
              <TableCell>Standard</TableCell>
              <TableCell>Here</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.type_conflicts.map((c) => (
              <TableRow key={c.name}>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  <PropertyName name={c.name} tenantCode={tenantCode} />
                </TableCell>
                <TableCell>{c.standard ?? '—'}</TableCell>
                <TableCell>{c.effective ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Undeclared attributes in the data"
        count={data.undeclared.length}
        caption="Keys tags actually carry that no schema mentions. These still render, but nothing documents or validates them."
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Attribute</TableCell>
              <TableCell align="right">Tags</TableCell>
              <TableCell>Sample</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.undeclared.map((u) => (
              <TableRow key={u.name}>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  <PropertyName name={u.name} tenantCode={tenantCode} />
                </TableCell>
                <TableCell align="right">{u.tags}</TableCell>
                <TableCell
                  sx={{ color: 'text.secondary', maxWidth: 360, overflowWrap: 'anywhere' }}
                >
                  {u.sample ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Per-language attributes"
        count={data.flat_multilingual.length}
        caption="Key families like notes_en / notes_es. A family that is not declared multilingual is invisible to {% tagattr %} — declaring the base fixes that at read time, with no change to the stored data. Only real language codes count, so product_id and reminders_20_hr are not listed here."
        defaultExpanded={needsMultilingual.length > 0}
      >
        {needsMultilingual.length > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
            {needsMultilingual.length} famil{needsMultilingual.length === 1 ? 'y is' : 'ies are'} not
            declared multilingual, so notifications cannot read{' '}
            {needsMultilingual.length === 1 ? 'it' : 'them'}.
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Base attribute</TableCell>
              <TableCell>Languages</TableCell>
              <TableCell align="right">Tags</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.flat_multilingual.map((f) => (
              <TableRow key={f.base}>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  <PropertyName name={f.base} tenantCode={tenantCode} />
                </TableCell>
                <TableCell>{f.languages.join(', ')}</TableCell>
                <TableCell align="right">{f.tags}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={f.declared_multilingual ? 'success' : 'warning'}
                    label={f.declared_multilingual ? 'folded at read time' : 'declare x-multilingual'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Typography variant="caption" color="text.secondary">
        Scanned {data.tags_scanned} tag{data.tags_scanned === 1 ? '' : 's'}
        {data.truncated ? ' (capped — the data findings are a sample, not a census).' : '.'}
      </Typography>
    </Stack>
  );
}
